import type { JSX } from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { SiteHeadRef } from '@/types/sites';

/**
 * Plan 09-02 D-88 ChangeSiteHeadConfirmDialog — confirms site-head reassignment
 * BEFORE the PUT /api/sites/:id fires. Surfaces both the previous head's name
 * and the incoming head's name so the admin sees the swap in plain language.
 */
export interface ChangeSiteHeadConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly previousSiteHead: SiteHeadRef | null;
  readonly newSiteHeadName: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ChangeSiteHeadConfirmDialog({
  open,
  onOpenChange,
  previousSiteHead,
  newSiteHeadName,
  onConfirm,
  onCancel,
}: ChangeSiteHeadConfirmDialogProps): JSX.Element {
  const previousLabel = previousSiteHead?.fullName ?? '— no head —';
  const newLabel = newSiteHeadName ?? '— no head —';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="change-sitehead-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Reassign Site Head?</AlertDialogTitle>
          <AlertDialogDescription>
            Reassigning Site Head from <strong>{previousLabel}</strong> to{' '}
            <strong>{newLabel}</strong>. The previous head will retain their access to other sites.
            Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel();
            }}
            data-testid="change-sitehead-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onConfirm();
            }}
            data-testid="change-sitehead-confirm"
          >
            Confirm reassignment
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
